import { Plus, X } from 'lucide-react';
import { AGENTS, DEPARTMENTS } from '../../utils/agents';
import { useTaskStore } from '../../stores/taskStore';

const PRIORITY_OPTIONS = ['urgent', 'high', 'medium', 'low'] as const;

const CADENCE_OPTIONS = [
  { value: 'weekly+', label: 'Weekly+' },
  { value: '', label: 'All Cadences' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'trigger', label: 'Trigger-Based' },
] as const;

interface TaskFiltersProps {
  onNewTask: () => void;
}

export default function TaskFilters({ onNewTask }: TaskFiltersProps) {
  const filters = useTaskStore((s) => s.filters);
  const setFilters = useTaskStore((s) => s.setFilters);

  const hasFilters = filters.agent || filters.department || filters.priority || (filters.cadence && filters.cadence !== 'weekly+');

  function clearFilters() {
    setFilters({ agent: undefined, department: undefined, priority: undefined, cadence: 'weekly+' });
  }

  return (
    <div className="bg-bg-secondary rounded-lg p-3 flex flex-row gap-3 items-center flex-wrap">
      <select
        value={filters.cadence || ''}
        onChange={(e) => setFilters({ cadence: e.target.value || undefined })}
        className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-1.5 text-sm"
      >
        {CADENCE_OPTIONS.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>

      <select
        value={filters.agent || ''}
        onChange={(e) => setFilters({ agent: e.target.value || undefined })}
        className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-1.5 text-sm"
      >
        <option value="">All Agents</option>
        {AGENTS.map((a) => (
          <option key={a.slug} value={a.slug}>
            {a.name}
          </option>
        ))}
      </select>

      <select
        value={filters.department || ''}
        onChange={(e) => setFilters({ department: e.target.value || undefined })}
        className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-1.5 text-sm"
      >
        <option value="">All Departments</option>
        {DEPARTMENTS.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>

      <select
        value={filters.priority || ''}
        onChange={(e) => setFilters({ priority: e.target.value || undefined })}
        className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-1.5 text-sm"
      >
        <option value="">All Priorities</option>
        {PRIORITY_OPTIONS.map((p) => (
          <option key={p} value={p}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </option>
        ))}
      </select>

      {hasFilters ? (
        <button
          onClick={clearFilters}
          className="text-text-muted hover:text-text-secondary text-sm flex items-center gap-1 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
          Clear
        </button>
      ) : null}

      <div className="flex-1" />

      <button
        onClick={onNewTask}
        className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors flex items-center gap-2"
      >
        <Plus className="h-4 w-4" />
        New Task
      </button>
    </div>
  );
}
